import {
  getCurrentUserDetails,
  refreshAccessToken,
  logoutByUserType,
} from "../routines/user-operations";
import { jwtDecode } from "jwt-decode";

/**
 * Per-role timers
 */
const refreshTimers: Record<string, ReturnType<typeof setTimeout> | null> = {};
const warningTimers: Record<string, ReturnType<typeof setTimeout> | null> = {};
const logoutTimers: Record<string, ReturnType<typeof setTimeout> | null> = {};

const lastActivity: Record<string, number> = {};

// Track activity
const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

// --- Provider requirement ---
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 min
const WARNING_AT_MS = 25 * 60 * 1000;       // warn at 25 min

export const msToMinutes = (ms: number) => (ms / (1000 * 60)).toFixed(2);

// Custom event name (so UI can listen without Redux changes)
const WARNING_EVENT = "SESSION_INACTIVITY_WARNING";

const emitWarningEvent = (payload: {
  userType: string;
  open: boolean;
  remainingMs?: number;
}) => {
  window.dispatchEvent(new CustomEvent(WARNING_EVENT, { detail: payload }));
};

/**
 * Clears timers + listeners only for the specified userType
 */
export const clearSessionTimers = (userType: string) => {
  if (refreshTimers[userType]) {
    clearTimeout(refreshTimers[userType]!);
    refreshTimers[userType] = null;
  }
  if (warningTimers[userType]) {
    clearTimeout(warningTimers[userType]!);
    warningTimers[userType] = null;
  }
  if (logoutTimers[userType]) {
    clearTimeout(logoutTimers[userType]!);
    logoutTimers[userType] = null;
  }

  // IMPORTANT: remove the SAME function reference you added
  // So we store a handler per userType
  removeActivityListeners(userType);
};

/**
 * ---- Activity listener handlers (must be stable references) ----
 */
const activityHandlers: Record<string, ((e: Event) => void) | null> = {};

const addActivityListeners = (userType: string) => {
  if (activityHandlers[userType]) return;

  activityHandlers[userType] = () => updateLastActivity(userType);

  activityEvents.forEach((ev) =>
    window.addEventListener(ev, activityHandlers[userType] as EventListener)
  );
};

const removeActivityListeners = (userType: string) => {
  if (!activityHandlers[userType]) return;

  activityEvents.forEach((ev) =>
    window.removeEventListener(ev, activityHandlers[userType] as EventListener)
  );

  activityHandlers[userType] = null;
};

/**
 * Initialize timers + activity listeners for a specific user type
 */
export const initSessionTimers = (userType: string = "user") => {
  console.log(`Starting session timers for userType = ${userType}`);

  // cleanup old timers for this userType only
  clearSessionTimers(userType);

  // initialize activity tracker
  lastActivity[userType] = Date.now();

  // add listeners
  addActivityListeners(userType);

  // schedule inactivity warning + logout
  resetInactivityTimers(userType);

  // start token refresh scheduling (your existing logic)
  scheduleProactiveTokenRefresh(userType);
};

/**
 * Updates last activity time for that userType
 * and resets warning/logout timers
 */
export const updateLastActivity = (userType: string) => {
  lastActivity[userType] = Date.now();

  // close warning if it was open
  emitWarningEvent({ userType, open: false });

  // reset inactivity timers
  resetInactivityTimers(userType);
};

/**
 * Schedules inactivity warning at 25 min and logout at 30 min
 */
const resetInactivityTimers = (userType: string) => {
  // clear existing
  if (warningTimers[userType]) clearTimeout(warningTimers[userType]!);
  if (logoutTimers[userType]) clearTimeout(logoutTimers[userType]!);

  const last = lastActivity[userType] || Date.now();
  const now = Date.now();

  const msSinceLast = now - last;
  const msUntilWarning = Math.max(WARNING_AT_MS - msSinceLast, 0);
  const msUntilLogout = Math.max(SESSION_DURATION_MS - msSinceLast, 0);

  // Schedule warning
  warningTimers[userType] = setTimeout(() => {
    // Only show warning if user is still inactive at this moment
    const idleMs = Date.now() - (lastActivity[userType] || 0);
    if (idleMs >= WARNING_AT_MS && idleMs < SESSION_DURATION_MS) {
      emitWarningEvent({
        userType,
        open: true,
        remainingMs: SESSION_DURATION_MS - idleMs, // ~5 min
      });
    }
  }, msUntilWarning);

  // Schedule hard logout
  logoutTimers[userType] = setTimeout(() => {
    const idleMs = Date.now() - (lastActivity[userType] || 0);
    if (idleMs >= SESSION_DURATION_MS) {
      console.info(`${userType} inactive for ${msToMinutes(idleMs)} mins → logging out`);
      logoutByUserType(userType, `${userType}:INACTIVITY`);
    }
  }, msUntilLogout);
};

/**
 * Your existing proactive refresh (kept)
 * (I left your logic mostly as-is)
 */
export const scheduleProactiveTokenRefresh = (userType: string) => {
  if (refreshTimers[userType]) clearTimeout(refreshTimers[userType]!);

  const currentUserDetails = getCurrentUserDetails(userType);
  const expiryTime = currentUserDetails?.expiryTime || 0;

  if (!expiryTime || expiryTime <= Date.now()) {
    console.warn(`Invalid or expired token expiry for ${userType}.`);
    // If token is already expired, just let ProtectedRoute / API 401 handle it,
    // OR force logout here if you prefer.
    return;
  }

  const refreshBeforeMs = 60 * 1000; // 1 minute before expiry
  const now = Date.now();
  const timeout = expiryTime - now - refreshBeforeMs;

  if (timeout <= 0) {
    console.warn(`Token too close to expiry for ${userType}. Refreshing now...`);
    // run refresh immediately instead of skipping
    doRefresh(userType);
    return;
  }

  refreshTimers[userType] = setTimeout(() => doRefresh(userType), timeout);
};

const doRefresh = async (userType: string) => {
  try {
    console.info(`Refreshing token (proactive) for ${userType}...`);
    const newAccessToken = await refreshAccessToken(userType);

    const decoded: any = jwtDecode(newAccessToken);
    if (decoded?.exp) {
      const newExpiry = decoded.exp * 1000;

      const keyName = `${userType}Details`;
      const details = getCurrentUserDetails(userType);
      details.expiryTime = newExpiry;

      localStorage.setItem(keyName, JSON.stringify(details));

      // reschedule next refresh
      scheduleProactiveTokenRefresh(userType);
    }
  } catch (err) {
    console.error(`Auto refresh failed for ${userType}:`, err);
    logoutByUserType(userType); // keep this (token refresh actually failed)
  }
};


// Export event name so UI can subscribe
export const SESSION_INACTIVITY_WARNING_EVENT = WARNING_EVENT;
